'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { buildImageUrl } from './imageUrl';
import type {
  ContextEntities,
  RenderContext,
  RenderMode,
  Renderable,
} from './RenderContext';
import { substitutePlaceholders } from './substitutePlaceholders';

const RenderContextContext = createContext<RenderContext | null>(null);

interface ProviderProps {
  mode: RenderMode;
  tagBag: ReadonlyArray<string>;
  renderable: Renderable;
  entities: ContextEntities;
  children: ReactNode;
}

export function RenderContextProvider({
  mode,
  tagBag,
  renderable,
  entities,
  children,
}: ProviderProps) {
  const value = useMemo<RenderContext>(() => {
    const tagBagArr = [...tagBag];
    return {
      mode,
      tagBag: tagBagArr,
      renderable,
      entities,
      imageUrl: (media, opts) => buildImageUrl(media, opts),
      substitutePlaceholders: (template) =>
        substitutePlaceholders(template, { entities, tagBag: tagBagArr }),
    };
  }, [mode, tagBag, renderable, entities]);

  return (
    <RenderContextContext.Provider value={value}>
      {children}
    </RenderContextContext.Provider>
  );
}

// Throws when used outside a RenderContextProvider — surfaces wiring
// mistakes loudly in dev rather than rendering broken text.
export function useRenderContext(): RenderContext {
  const ctx = useContext(RenderContextContext);
  if (!ctx) {
    throw new Error(
      'useRenderContext used outside <RenderContextProvider>. Mount the provider in your route.',
    );
  }
  return ctx;
}

// Optional read — returns null instead of throwing. Useful for shared
// components that may render in either provider'd or non-provider'd trees.
export function useOptionalRenderContext(): RenderContext | null {
  return useContext(RenderContextContext);
}

const identity = (s: string) => s;

// Placeholder-substitution function for plain-string widget fields. Falls
// back to identity when rendered outside a provider (e.g. unit tests), so
// widgets can call `const sub = useSubstitute()` without a null guard.
export function useSubstitute(): (s: string) => string {
  return useOptionalRenderContext()?.substitutePlaceholders ?? identity;
}
